import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  adminApi,
  AdminUser,
  AdminTenant,
  CreateUserPayload,
  UpdateUserPayload,
  tenantUserApi,
  clusterApi,
  TenantUserCreatePayload,
  TenantUserUpdatePayload,
} from "../../services/api";
import { parseServerDate } from "../../utils/formatters";
import { useAuth } from "../../contexts/AuthContext";
import { MyLocalStorage } from "../../utils/storage";
import ConfirmationModal from "../ConfirmationModal";
import CreateUserModal from "./CreateUserModal";
import EditUserModal from "./EditUserModal";
import { useToast } from "../Toast";
import GroupIcon from "@mui/icons-material/Group";

type StatusFilter = "all" | "active" | "inactive";

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [confirmationUser, setConfirmationUser] = useState<AdminUser | null>(null);
  const [confirmationAction, setConfirmationAction] = useState<
    "activate" | "deactivate" | null
  >(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateUserPayload>({
    email: "",
    tenant_id: "",
    role: "user",
    is_superadmin: false,
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateUserPayload>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSuperadmin = currentUser?.is_superadmin ?? false;
  const isTenantAdmin = currentUser?.role === "admin" && !isSuperadmin;
  const canManageUsers = isSuperadmin || isTenantAdmin;

  // Refs to prevent duplicate fetches
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch if we haven't already and not currently fetching
    if (!hasFetchedRef.current && currentUser) {
      loadData();
    }
  }, [currentUser]);

  // Filter users based on status
  const filteredUsers = useMemo(() => {
    if (statusFilter === "all") return users;
    return users.filter((user) =>
      statusFilter === "active" ? user.is_active : !user.is_active
    );
  }, [users, statusFilter]);

  const loadData = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    // Skip if already fetched unless force refresh
    if (hasFetchedRef.current && !forceRefresh) return;
    
    isFetchingRef.current = true;
    setIsLoading(true);
    setError("");
    try {
      if (!currentUser) {
        setUsers([]);
        setTenants([]);
        return;
      }

      if (isSuperadmin) {
        const [usersData, tenantsData] = await Promise.all([
          adminApi.listUsers(),
          adminApi.listTenants(),
        ]);
        setUsers(usersData);
        setTenants(tenantsData);
        hasFetchedRef.current = true;
        return;
      }

      const [tenantUsers, tenantInfo] = await Promise.all([
        tenantUserApi.listUsers(),
        clusterApi.getCurrentTenant(),
      ]);

      const mappedUsers: AdminUser[] = tenantUsers.map((tenantUser) => ({
        ...tenantUser,
        tenant_name: tenantInfo.name,
        is_superadmin: false,
      }));

      setUsers(mappedUsers);
      setTenants([
        {
          id: tenantInfo.id,
          name: tenantInfo.name,
          is_active: tenantInfo.is_active,
          serp_preview_enabled: tenantInfo.serp_preview_enabled,
          user_count: mappedUsers.length,
          created_at: tenantInfo.created_at ?? null,
          updated_at: tenantInfo.updated_at ?? null,
        },
      ]);
      hasFetchedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [currentUser, isSuperadmin]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    try {
      if (!currentUser) {
        throw new Error("User context unavailable");
      }

      if (isSuperadmin) {
        await adminApi.createUser(formData);
      } else {
        const payload: TenantUserCreatePayload = {
          email: formData.email,
          tenant_id: currentUser.tenant_id,
          role: formData.role,
          is_active: true,
        };
        await tenantUserApi.createUser(payload);
      }
      setShowCreateModal(false);
      setFormData({
        email: "",
        tenant_id: "",
        role: "user",
        is_superadmin: false,
      });
      showToast("success", "Invite sent");
      loadData(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvite = async (userId: string) => {
    try {
      if (isSuperadmin) {
        await adminApi.resendInvite(userId);
      } else {
        await tenantUserApi.resendInvite(userId);
      }
      showToast("success", "Invite email resent");
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Failed to resend invite");
    }
  };

  const handlePreviewAs = async (userId: string) => {
    try {
      const response = await adminApi.loginAs(userId);

      // Backup current admin token
      const currentToken = MyLocalStorage.jwt_token.get();
      if (currentToken) {
        MyLocalStorage.jwt_token_admin.set(currentToken);
      }

      // Replace with impersonated user token
      MyLocalStorage.jwt_token.set(response.access_token);

      // Redirect to home and refresh
      window.location.href = "/";
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Failed to preview as user");
    }
  };

  const handleEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setFormError("");
    setIsSubmitting(true);

    try {
      if (isSuperadmin) {
        await adminApi.updateUser(selectedUser.id, updateFormData);
      } else {
        const payload: TenantUserUpdatePayload = {};
        if (updateFormData.email !== undefined) payload.email = updateFormData.email;
        if (updateFormData.role !== undefined) payload.role = updateFormData.role;
        if (updateFormData.is_active !== undefined) {
          payload.is_active = updateFormData.is_active;
        }
        await tenantUserApi.updateUser(selectedUser.id, payload);
      }
      setShowEditModal(false);
      setSelectedUser(null);
      setUpdateFormData({});
      loadData(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleUserStatus = (user: AdminUser) => {
    const action = user.is_active ? "deactivate" : "activate";
    setConfirmationUser(user);
    setConfirmationAction(action);
    setIsConfirmationOpen(true);
  };

  const handleConfirmToggleUserStatus = async () => {
    if (!confirmationUser || !confirmationAction) return;
    const action = confirmationAction;
    try {
      if (isSuperadmin) {
        if (confirmationUser.is_active) {
          await adminApi.deleteUser(confirmationUser.id);
        } else {
          await adminApi.updateUser(confirmationUser.id, { is_active: true });
        }
      } else {
        if (confirmationUser.is_active) {
          await tenantUserApi.deleteUser(confirmationUser.id);
        } else {
          await tenantUserApi.updateUser(confirmationUser.id, { is_active: true });
        }
      }
      loadData(true);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error
          ? err.message
          : `Failed to ${action} user`
      );
    } finally {
      setIsConfirmationOpen(false);
      setConfirmationUser(null);
      setConfirmationAction(null);
    }
  };

  const openEditModal = (user: AdminUser) => {
    setSelectedUser(user);
    if (isSuperadmin) {
      setUpdateFormData({
        email: user.email,
        tenant_id: user.tenant_id,
        role: user.role,
        is_active: user.is_active,
        is_superadmin: user.is_superadmin,
      });
    } else {
      setUpdateFormData({
        email: user.email,
        role: user.role,
        is_active: user.is_active,
      });
    }
    setFormError("");
    setShowEditModal(true);
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        {error}
        <button onClick={() => loadData(true)} style={{ marginLeft: 16 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-section-header">
        <h2>User Management</h2>
        <div className="admin-header-actions">
          <div className="admin-filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="admin-filter-select"
            >
              <option value="all">All ({users.length})</option>
              <option value="active">
                Active ({users.filter((u) => u.is_active).length})
              </option>
              <option value="inactive">
                Inactive ({users.filter((u) => !u.is_active).length})
              </option>
            </select>
          </div>
          {canManageUsers && (
            <button
              className="admin-add-btn"
              onClick={() => {
                if (currentUser && !currentUser.email_verified) {
                  showToast("error", "Please verify your email address first before using this app!")
                  return;
                }

                setFormError("");
                if (currentUser && !isSuperadmin) {
                  setFormData({
                    ...formData,
                    tenant_id: currentUser.tenant_id,
                    is_superadmin: false,
                  });
                }
                setShowCreateModal(true);
              }}
            >
              <span>+</span> Add User
            </button>
          )}
        </div>
      </div>

      {filteredUsers.length === 0 ? (
        <div className="admin-empty">
          <GroupIcon className="admin-empty-icon" sx={{ fontSize: 48 }} />
          <p>
            {statusFilter === "all"
              ? "No users found. Create your first user to get started."
              : `No ${statusFilter} users found.`}
          </p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Tenant</th>
                <th>Role</th>
                <th>Status</th>
                <th>Created</th>
                {canManageUsers && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((rowUser) => (
                <tr key={rowUser.id}>
                  <td>
                    {rowUser.email}
                    {rowUser.is_superadmin && (
                      <span className="badge badge-superadmin" style={{ marginLeft: 8 }}>
                        Superadmin
                      </span>
                    )}
                  </td>
                  <td>{rowUser.tenant_name || rowUser.tenant_id}</td>
                  <td>
                    <span className={`badge badge-${rowUser.role}`}>
                      {rowUser.role}
                    </span>
                  </td>
                  <td>
                    {rowUser.invite_pending ? (
                      <span className="badge badge-warning">Invite Pending</span>
                    ) : (
                      <span
                        className={`badge ${
                          rowUser.is_active ? "badge-active" : "badge-inactive"
                        }`}
                      >
                        {rowUser.is_active ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>
                  <td>
                    {rowUser.created_at
                      ? parseServerDate(rowUser.created_at).toLocaleDateString()
                      : "-"}
                  </td>
                  {canManageUsers && (
                    <td>
                      <div className="admin-actions">
                        <button
                          className="admin-action-btn"
                          onClick={() => openEditModal(rowUser)}
                        >
                          Edit
                        </button>
                        {rowUser.invite_pending && (
                          <button
                            className="admin-action-btn"
                            onClick={() => handleResendInvite(rowUser.id)}
                          >
                            Resend Invite
                          </button>
                        )}
                        {isSuperadmin && rowUser.is_active && !rowUser.invite_pending && !rowUser.is_superadmin && (
                          <button
                            className="admin-action-btn"
                            onClick={() => handlePreviewAs(rowUser.id)}
                            style={{ backgroundColor: "#4299e1", color: "white" }}
                          >
                            Preview As
                          </button>
                        )}
                        <button
                          className={`admin-action-btn ${
                            rowUser.is_active ? "delete" : "activate"
                          }`}
                          onClick={() => handleToggleUserStatus(rowUser)}
                        >
                          {rowUser.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateUserModal
        isOpen={showCreateModal && canManageUsers}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateUser}
        formData={formData}
        setFormData={setFormData}
        tenants={tenants}
        isSuperadmin={isSuperadmin}
        currentUserTenantId={currentUser?.tenant_id}
        currentTenantName={tenants[0]?.name}
        formError={formError}
        isSubmitting={isSubmitting}
      />

      <EditUserModal
        isOpen={showEditModal && !!selectedUser && canManageUsers}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleEditUser}
        selectedUser={selectedUser}
        updateFormData={updateFormData}
        setUpdateFormData={setUpdateFormData}
        tenants={tenants}
        isSuperadmin={isSuperadmin}
        formError={formError}
        isSubmitting={isSubmitting}
      />

      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => {
          setIsConfirmationOpen(false);
          setConfirmationUser(null);
          setConfirmationAction(null);
        }}
        onConfirm={handleConfirmToggleUserStatus}
        title={
          confirmationAction === "deactivate" ? "Deactivate user" : "Activate user"
        }
        message={
          confirmationAction === "deactivate"
            ? "Are you sure you want to deactivate this user?"
            : "Are you sure you want to activate this user?"
        }
        confirmText={confirmationAction === "deactivate" ? "Deactivate" : "Activate"}
        variant={confirmationAction === "deactivate" ? "danger" : "info"}
      />
    </div>
  );
};

export default UserManagement;
